<template>
  <div class="kill-loader" v-if="!isReady">
    <div class="preloader-wrapper active">
      <div class="spinner-layer spinner-red-only">
        <div class="circle-clipper left">
          <div class="circle"></div>
        </div>
        <div class="gap-patch">
          <div class="circle"></div>
        </div>
        <div class="circle-clipper right">
          <div class="circle"></div>
        </div>
      </div>
    </div>
  </div>

  <form @submit.prevent="handleSubmit" v-else>
    <div class="card-panel kill-confirmation">
      <div class="kill-confirmation-icon">
        <i class="material-icons">block</i>
      </div>

      <h5>Block active access?</h5>
      <p class="confirmation-copy grey-text text-darken-1">
        This is an admin-level action for records already in the Bin. It keeps the family inactive and closes any current active enrollee access from yesterday.
      </p>

      <div class="admin-warning">
        <i class="material-icons">warning</i>
        <span>
          Confirm that billing, reporting, and any historic encounter references have been reviewed before proceeding.
        </span>
      </div>

      <div class="kill-summary">
        <div>
          <span>Principal</span>
          <strong>{{ enrollee.name }}</strong>
        </div>
        <div>
          <span>Enrollee ID</span>
          <strong>{{ enrollee.enrollee_number || "Not assigned" }}</strong>
        </div>
        <div>
          <span>Current Location</span>
          <strong>Bin</strong>
        </div>
        <div>
          <span>After Confirmation</span>
          <strong>Inactive with access blocked</strong>
        </div>
      </div>

      <p class="red-text text-darken-2" v-if="error">{{ error }}</p>

      <div class="kill-actions">
        <a
          href="#"
          class="btn-flat waves-effect"
          :class="{ disabled: isSubmitting }"
          @click.prevent="handleBack"
        >
          Cancel
        </a>
        <button
          class="waves-effect waves-light btn-large red darken-2"
          :class="{ disabled: !isReady || isSubmitting }"
        >
          <i class="material-icons right">block</i>
          {{ isSubmitting ? "Blocking Access" : `Block Access for ${enrollee.name}` }}
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { computed, ref } from "vue";
import putItem from "@/composables/putItem";

export default {
  name: "EnrolleeKillAccountOnboardComponent",
  props: ["enrollee"],
  emits: ["reset"],
  setup(props, context) {
    const isSubmitting = ref(false);
    const error = ref(null);

    const isReady = computed(() => {
      return Boolean(
        props.enrollee?.id &&
        props.enrollee?.name &&
        props.enrollee?.status == "d"
      );
    });

    // handle back
    const handleBack = () => {
      context.emit("reset", null);
    };

    //handle submit
    const handleSubmit = async () => {
      // Native form validation handles custom errors \\

      // start
      isSubmitting.value = true;
      error.value = null;

      // round up
      const newVal = {
        type: "p", // principal
        status: "d",
      };

      // post item
      const { res, err } = await putItem(
        "operator/client/enrollee/status/kill",
        props.enrollee.id,
        newVal
      );

      if (res.value) {
        // start
        let principal;
        // next
        principal = {
          ...props.enrollee,
          ...res.value.data,
        };

        // go back
        context.emit("reset", principal);
      } else {
        error.value = err.value;
      }

      // show button back
      isSubmitting.value = false;
    };

    return {
      handleBack,
      handleSubmit,
      error,
      isReady,
      isSubmitting,
    };
  },
};
</script>

<style scoped>
.kill-loader {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 260px;
}

.kill-confirmation {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  box-shadow: none;
  margin-bottom: 0;
  padding: 24px;
  text-align: center;
}

.kill-confirmation h5 {
  color: #263238;
  font-weight: 600;
  margin: 12px 0 8px;
}

.confirmation-copy {
  margin-left: auto;
  margin-right: auto;
  max-width: 620px;
}

.kill-confirmation-icon {
  align-items: center;
  background: #ffebee;
  border-radius: 50%;
  color: #c62828;
  display: inline-flex;
  height: 58px;
  justify-content: center;
  width: 58px;
}

.kill-confirmation-icon i {
  font-size: 30px;
}

.admin-warning {
  align-items: center;
  background: #fff3e0;
  border-left: 4px solid #ef6c00;
  border-radius: 4px;
  color: #4e342e;
  display: flex;
  gap: 10px;
  margin: 18px auto 0;
  max-width: 620px;
  padding: 10px 12px;
  text-align: left;
}

.admin-warning i {
  color: #ef6c00;
  font-size: 21px;
}

.kill-summary {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin: 22px auto;
  max-width: 620px;
  overflow: hidden;
  text-align: left;
}

.kill-summary div {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(130px, 220px) 1fr;
  padding: 13px 16px;
}

.kill-summary div:last-child {
  border-bottom: 0;
}

.kill-summary span {
  color: #607d8b;
  font-size: 0.85rem;
  font-weight: 600;
}

.kill-summary strong {
  color: #263238;
  font-weight: 500;
}

.kill-actions {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 24px;
}

@media (max-width: 600px) {
  .kill-summary div {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  .kill-actions {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
